import React, { useState } from 'react';

import { type WaypointFeatureTier } from '@dashboard/generated/wp/platform';
import { EditOutlined, LockOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Elements, ElementsConsumer, PaymentElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { getLabel, getPrice } from '../helpers';

const stripePromise = loadStripe(WAYPOINT_CONFIGURATION.stripeElementsKey);

type Props = {
  tier: WaypointFeatureTier;
  onClose: () => void;
  reset: () => void;
  onSubmit: (props: { paymentMethodId: string; tier: WaypointFeatureTier }) => void;
  loading: boolean;
};

export default function CreditCardInfoDialog({ tier, onClose, onSubmit, reset, loading }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const isSubmitDisabled = submitting || loading;
  return (
    <Dialog open onClose={onClose}>
      <Elements stripe={stripePromise} options={{ paymentMethodCreation: 'manual', mode: 'setup', currency: 'usd' }}>
        <ElementsConsumer>
          {({ stripe, elements }) => (
            <form
              onSubmit={async (ev) => {
                ev.preventDefault();
                setSubmitting(true);
                if (elements === null || stripe === null) {
                  return;
                }
                await elements.submit();
                const res = await stripe.createPaymentMethod({ elements });
                setSubmitting(false);
                if (res.paymentMethod) {
                  await onSubmit({ tier, paymentMethodId: res.paymentMethod.id });
                  onClose();
                }
              }}
            >
              <DialogTitle>Manage plan</DialogTitle>
              <DialogContent>
                <Stack spacing={3} mt={2}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h6">Waypoint {getLabel(tier)}</Typography>
                        <IconButton onClick={() => reset()} size="small" color="secondary">
                          <EditOutlined sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                      <Typography color="text.secondary">What you'll pay monthly starting today</Typography>
                    </Box>
                    <Typography variant="h6">{getPrice(tier)}</Typography>
                  </Stack>
                  <Divider />
                  <Box minHeight={280}>
                    <PaymentElement />
                  </Box>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                    <LockOutlined sx={{ fontSize: 13, color: 'grey.600' }} />
                    <Typography variant="body2" color="text.secondary">
                      Payments securely processed through Stripe
                    </Typography>
                  </Stack>
                  <Box>
                    <Button onClick={onClose} color="secondary" disabled={isSubmitDisabled}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={isSubmitDisabled}>
                      Upgrade plan
                    </Button>
                  </Box>
                </Stack>
              </DialogActions>
            </form>
          )}
        </ElementsConsumer>
      </Elements>
    </Dialog>
  );
}
